
import { computed, defineComponent, watch, WritableComputedRef } from 'vue';
import dictionaryService from '@/core/dictionary/dictionary.service';
import useCallingCodes from '@/shared/address/calling-codes.composable';
import { Field, useField } from 'vee-validate';
import { getMaybeRequiredLabel, useValidationSchema } from '@/shared/form/useRequiredForm';

export default defineComponent({
    name: 'InputPhone',
    components: { Field },
    props: {
        name: {
            type: String,
            required: true,
        },
        id: {
            type: String,
            required: false,
            default: '',
        },
        label: {
            type: String,
            default: dictionaryService.get('global/generic/phone'),
            required: false,
        },
        rules: {
            type: String,
            default: '',
        },
    },
    emits: {
        'update:modelValue': (value) => true,
    },
    setup(props, { emit }) {
        const internalCodeFieldName = `__internalCodeName__${props.name}`;
        const internalPhoneFieldName = `__internalPhoneName__${props.name}`;

        const fieldRules = computed(() => props.rules);

        const { value: externalValue } = useField<string | undefined>(props.name, fieldRules.value);
        const { setValue: setInternalCodeValue } = useField<string | undefined>(internalCodeFieldName);
        const { setValue: setInternalPhoneValue } = useField<string | undefined>(internalPhoneFieldName);

        const {
            codes,
            stripCode,
            formatCodes,
            mostRelevantCodeForSite,
            getMatchingCodeString,
        } = useCallingCodes();

        watch(externalValue, (value) => {
            setInternalCodeValue(getMatchingCodeString(value) || mostRelevantCodeForSite.value);
            setInternalPhoneValue(stripCode(value));
        });

        const internalPhoneModel: WritableComputedRef<string> = computed({
            get: () => {
                return stripCode(externalValue.value);
            },
            set(value: string) {
                externalValue.value = value ? internalCodeModel.value + stripCode(value) : '';
            },
        });

        const internalCodeModel: WritableComputedRef<string> = computed({
            get: () => {
                return getMatchingCodeString(externalValue.value) || mostRelevantCodeForSite.value;
            },
            set(value: string) {
                externalValue.value = value + internalPhoneModel.value;
            },
        });

        const schema = useValidationSchema();
        const maybeRequiredLabel = computed<string>(() => getMaybeRequiredLabel(props.name, schema, props.label));

        return {
            externalValue,
            codes,
            formatCodes,
            internalCodeModel,
            internalPhoneModel,
            maybeRequiredLabel,
            internalCodeFieldName,
            internalPhoneFieldName,
        };
    },
});
